// src/slices/unsoldTicketReturn/thunk.ts
// Async thunks cho US-016 (tiếp nhận & kiểm đếm vé ế tại quầy) + US-017 (lập phiếu đối soát).
// Request/response bám đúng UnsoldTicketReturnRequestController + TicketReturnReconciliationController.

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getUnsoldTicketReturnRequestsApi,
  getUnsoldTicketReturnRequestByIdApi,
  createUnsoldTicketReturnRequestApi,
  scanCountUnsoldTicketApi,
  createTicketReturnReconciliationApi,
  getTicketReturnReconciliationByIdApi,
} from 'helpers/fakebackend_helper';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_return_messages');

interface FetchReturnRequestsParams {
  // LƯU Ý: BE hiện hiểu `AgentCode` chính là AgentId dạng chuỗi số (chưa có API resolve theo Code
  // ở system-service) — nên FE dùng thẳng `AgentId` cho rõ ràng.
  agentId?: number | string;
  code?: string;
  status?: number | string;
  pageIndex?: number;
  pageSize?: number;
}

const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Tìm yêu cầu hoàn vé ế — SearchUnsoldTicketReturnRequestsQuery (PascalCase query param)
export const fetchUnsoldTicketReturnRequests = createAsyncThunk(
  'UnsoldTicketReturn/fetchList',
  async (params: FetchReturnRequestsParams, { rejectWithValue }) => {
    try {
      const response = await getUnsoldTicketReturnRequestsApi(
        cleanParams({
          AgentId: params.agentId,
          Code: params.code,
          Status: params.status,
          PageIndex: params.pageIndex,
          PageSize: params.pageSize,
        }),
      );
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tim_yeu_cau_hoan_message'));
    }
  },
);

// Chi tiết 1 yêu cầu (kèm các dòng khai báo của đại lý)
export const fetchUnsoldTicketReturnRequestById = createAsyncThunk(
  'UnsoldTicketReturn/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getUnsoldTicketReturnRequestByIdApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_chi_tiet_yeu_message'));
    }
  },
);

// Tạo yêu cầu hoàn vé ế (API dùng chung với App Đại lý — US-014/US-015), body JSON
export const createUnsoldTicketReturnRequest = createAsyncThunk(
  'UnsoldTicketReturn/create',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await createUnsoldTicketReturnRequestApi(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tao_yeu_cau_hoan_message'));
    }
  },
);

/**
 * Ghi nhận các vé vừa quét. Gọi được nhiều lần, server cộng dồn vào phiếu đối soát đang mở.
 * Máy quét cầm tay bắn từng vé một nên mỗi lần Enter gửi đúng 1 phần tử `tickets`.
 */
export const scanCountUnsoldTicket = createAsyncThunk(
  'UnsoldTicketReturn/scanCount',
  async (
    {
      id,
      ticketNumber,
      symbol,
      rejectReason,
    }: { id: number; ticketNumber: string; symbol: string; rejectReason?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await scanCountUnsoldTicketApi(id, [
        cleanParams({ ticketNumber, symbol, rejectReason }) as {
          ticketNumber: string;
          symbol: string;
          rejectReason?: string;
        },
      ]);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue({
        ticketNumber,
        message: error?.message || st('khong_ghi_nhan_duoc_ve_vua_text'),
      });
    }
  },
);

// Nạp phiếu đối soát đang mở (theo reconciliationId từ kết quả scan-count) để hiển thị đầy đủ
// các vé đã quét cộng dồn, thay vì chỉ các vé của lần quét cuối.
export const fetchScanReconciliation = createAsyncThunk(
  'UnsoldTicketReturn/fetchScanReconciliation',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getTicketReturnReconciliationByIdApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_chi_tiet_phieu_message'));
    }
  },
);

/**
 * Lập phiếu đối soát (US-017). Controller khai báo [Consumes("multipart/form-data")] nên LUÔN gửi
 * FormData kể cả khi không đính kèm biên bản.
 */
export const createTicketReturnReconciliation = createAsyncThunk(
  'UnsoldTicketReturn/createReconciliation',
  async (
    {
      returnRequestId,
      note,
      allowMissing,
      attachment,
    }: { returnRequestId: number; note?: string; allowMissing: boolean; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append('returnRequestId', String(returnRequestId));
      if (note) formData.append('note', note);
      formData.append('allowMissing', String(allowMissing));
      if (attachment) formData.append('attachment', attachment);

      const response = await createTicketReturnReconciliationApi(formData);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_lap_phieu_doi_soat_message'));
    }
  },
);
